import React, { useState } from 'react';
import api from '../services/api';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

const CouponInput = ({ onCouponApplied, tier, amount }) => {
  const [couponCode, setCouponCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null);

  const validateCoupon = async () => {
    if (!couponCode.trim()) {
      setMessage({ type: 'error', text: 'Please enter a coupon code' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      // amount prop is passed from Checkout as a string like "297" (dollars).
      // Backend expects an integer amount in cents. Convert here.
      const amountInCents = Math.round(parseFloat(amount) * 100);

      const response = await api.post('/coupons/validate', {
        code: couponCode.toUpperCase(),
        tier,
        amount: amountInCents,
      });

      if (response.data.valid) {
        // Normalize response to include code at top level and formatted final_price
        const normalized = {
          ...response.data,
          code: response.data.coupon.code,
          final_price: `$${(response.data.final_amount / 100).toFixed(2)}`,
        };

        setAppliedCoupon(normalized);
        setMessage({ 
          type: 'success', 
          text: `${response.data.coupon.discount_text} applied! You save $${(response.data.discount / 100).toFixed(2)}` 
        });
        onCouponApplied(normalized);
      } else {
        setMessage({ type: 'error', text: response.data.message });
        setAppliedCoupon(null);
        onCouponApplied(null);
      }
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.message || 'Invalid coupon code' 
      });
      setAppliedCoupon(null);
      onCouponApplied(null);
    } finally {
      setLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(null);
    setMessage(null);
    onCouponApplied(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        <input
          type="text"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
          placeholder="Enter coupon code"
          disabled={appliedCoupon}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
          onKeyPress={(e) => e.key === 'Enter' && validateCoupon()}
        />
        
        {!appliedCoupon ? (
          <button
            onClick={validateCoupon}
            disabled={loading || !couponCode.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Checking...' : 'Apply'}
          </button>
        ) : (
          <button
            onClick={removeCoupon}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Remove
          </button>
        )}
      </div>

      {message && (
        <div className={`flex items-center space-x-2 p-3 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.type === 'success' ? (
            <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
          ) : (
            <XCircleIcon className="h-5 w-5 flex-shrink-0" />
          )}
          <span className="text-sm font-medium">{message.text}</span>
        </div>
      )}

      {appliedCoupon && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-900">{appliedCoupon.coupon.name}</p>
              <p className="text-xs text-gray-600 mt-1">Code: <code className="font-mono bg-white px-2 py-0.5 rounded">{appliedCoupon.coupon.code}</code></p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-purple-600">
                -${(appliedCoupon.discount / 100).toFixed(2)}
              </p>
              <p className="text-xs text-gray-600">Savings</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponInput;
